/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisStepper } from 'src/core/components/luisStepper/LuisStepper';
import { LuisStepperStepType } from 'src/core/components/luisStepper/LuisStepperStep';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';

const messages = defineMessages({
    title: {
        id: 'luis.deepStackTrainingModel.title',
        defaultMessage: 'Train your app'
    },
    trainWithNeuralModel: {
        id: 'luis.deepStackTrainingModel.trainWithNeuralModel',
        defaultMessage: 'Train on Neural Models (Preview)'
    },
    trainStandardModel: {
        id: 'luis.deepStackTrainingModel.trainStandardModel',
        defaultMessage: 'Train on Standard NLU Models (GA)'
    },
    patternsBullet: {
        id: 'luis.deepStackTrainingModel.patternsBullet',
        defaultMessage: 'Patterns will not be used for intent classification, and pattern.any entities will not be extracted'
    },
    phraseListsBullet: {
        id: 'luis.deepStackTrainingModel.phraseListsBullet',
        defaultMessage: 'Phrase list features'
    },
    normalizationBullet: {
        id: 'luis.deepStackTrainingModel.normalizationBullet',
        defaultMessage: 'Version settings: Normalize punctuation​, and Normalize word forms​'
    },
    modelFeaturesBullet: {
        id: 'luis.deepStackTrainingModel.modelFeaturesBullet',
        defaultMessage: 'Model features: entities as features to other entities and intents'
    },
    rolesBullet: {
        id: 'luis.deepStackTrainingModel.rolesBullet',
        defaultMessage: 'Roles'
    },
    overlappingLabelsBullet: {
        id: 'luis.deepStackTrainingModel.overlappingLabelsBullet',
        defaultMessage: `Overlapping labels of independent machine-learned entities. In the event of an overlap, the longer label will be
        used and the shorter labels will be ignored.`
    },
    containerizedRuntimeBullet: {
        id: 'luis.deepStackTrainingModel.containerizedRuntimeBullet',
        defaultMessage: 'Containerized run-times.'
    },
    learnMoreUnsupportedFeatures: {
        id: 'luis.deepStackTrainingModel.learnMoreUnsupportedFeatures',
        defaultMessage: 'Learn more about the unsupported private preview features.​'
    },
    timeNote: {
        id: 'luis.deepStackTrainingModel.timeNote',
        defaultMessage: 'Note: Expect neural model training times up to 15 minutes.'
    },
    benefitsStepTitle: {
        id: 'luis.deepStackTrainingModel.benefitsStepTitle',
        defaultMessage: 'Neural Model Benefits'
    },
    benefitsTitle: {
        id: 'luis.deepStackTrainingModel.benefitsTitle',
        defaultMessage: 'What happens when you train a neural model?'
    },
    limitationsStepTitle: {
        id: 'luis.deepStackTrainingModel.limitationsStepTitle',
        defaultMessage: 'Unsupported Features'
    },
    limitationsTitle: {
        id: 'luis.deepStackTrainingModel.limitationsTitle',
        defaultMessage: 'What does not affect on your model?'
    },
    stateOfArt: {
        id: 'luis.deepStackTrainingModel.stateOfArt',
        defaultMessage: 'State-of-the-art'
    },
    stateOfArtDescription: {
        id: 'luis.deepStackTrainingModel.stateOfArtDescription',
        defaultMessage: 'natural language understanding technology using advanced neural networks'
    },
    robust: {
        id: 'luis.deepStackTrainingModel.robust',
        defaultMessage: 'Robust'
    },
    robustCont: {
        id: 'luis.deepStackTrainingModel.robustCont',
        defaultMessage: 'and semantically aware classification and extraction models'
    },
    simplerAppExperienceIntro: {
        id: 'luis.deepStackTrainingModel.simplerAppExperienceIntro',
        defaultMessage: 'Fewer options and dials providing a'
    },
    simplerAppExperience: {
        id: 'luis.deepStackTrainingModel.simplerAppExperience',
        defaultMessage: 'simpler application building experience'
    },
    testMultiLanguageIntro: {
        id: 'luis.deepStackTrainingModel.testMultiLanguageIntro',
        defaultMessage: 'Natively multilingual models that allow you to'
    },
    testMultiLanguage: {
        id: 'luis.deepStackTrainingModel.testMultiLanguage',
        defaultMessage: 'train in one language and test in others'
    }
});

const { styleUlist, styleLi } = getStylistV2('DeepStackTrainingModel');
const ListItem = styleLi('ListItem', { margin: '4px 16px' });
const ShiftedUList = styleUlist('ShiftedUList', { listStyle: 'unset', paddingInlineStart: '15px' });

/**
 * The step index of the neural training modal.
 */
enum NeuralTrainingModalSteps {
    benefitsStep = 'benefitsStep',
    limitationsStep = 'limitationsStep'
}

/**
 * Displays the modal that has the information of
 * the deep stack training.
 */
export const DeepStackTrainingModal = withModalMounter((props: BasicModalProps<object>) => {
    const { onSubmit } = props;
    const { localize } = useLocalization();
    const { dispatcher } = useGlobalState();
    const { isLargeOrGreater } = useResponsiveMode();

    const trainNeuralModelButtonId = React.useRef(getId('neuralModelTrainButton'));
    const [activeStep, setActiveStep] = React.useState<NeuralTrainingModalSteps>(NeuralTrainingModalSteps.benefitsStep);

    const getStepState = (step: NeuralTrainingModalSteps): LuisStepperStepType['state'] => {
        if (step === activeStep) {
            return 'active';
        }
        if (step === NeuralTrainingModalSteps.benefitsStep && activeStep === NeuralTrainingModalSteps.limitationsStep) {
            return 'completed';
        }

        return 'none';
    };

    const modalSteps: LuisStepperStepType[] = [
        {
            id: NeuralTrainingModalSteps.benefitsStep,
            title: localize(messages.benefitsStepTitle),
            state: getStepState(NeuralTrainingModalSteps.benefitsStep)
        },
        {
            id: NeuralTrainingModalSteps.limitationsStep,
            title: localize(messages.limitationsStepTitle),
            state: getStepState(NeuralTrainingModalSteps.limitationsStep)
        }
    ];

    const getStepContent = () => {
        switch (activeStep) {
            case NeuralTrainingModalSteps.benefitsStep:
                return <BenefitsScreen />;
            case NeuralTrainingModalSteps.limitationsStep:
                return <LimitationsScreen />;
            default:
        }
    };

    const onStepChange = (stepKey: string) => setActiveStep(stepKey as NeuralTrainingModalSteps);

    const runNeuralModelTrain = () => {
        dispatcher.dispatch('train', { mode: 'Neural' });
        onSubmit();
    };

    const runRegularTrain = () => {
        dispatcher.dispatch('train');
        onSubmit();
    };

    const footer: React.ReactNode = [
        <LuisTooltip
            key="trainNeuralModelButton"
            content={localize(messages.timeNote)}
            calloutProps={{ target: `#${trainNeuralModelButtonId.current}`, directionalHint: DirectionalHint.topAutoEdge }}>
            <PrimaryButton
                onClick={runNeuralModelTrain}
                data-automation-id="trainNeuralModel"
                id={trainNeuralModelButtonId.current}
                text={localize(messages.trainWithNeuralModel)}
            />
        </LuisTooltip>,
        <DefaultButton
            onClick={runRegularTrain}
            key="trainRegularModelButton"
            data-automation-id="trainRegularModel"
            text={localize(messages.trainStandardModel)}
        />
    ];

    return (
        <BasicModal {...props} valid minWidth={750} onRenderFooter={() => footer} dialogContentProps={{ title: localize(messages.title) }}>
            <Stack horizontal={isLargeOrGreater} verticalFill tokens={{ childrenGap: 70 }}>
                <Stack.Item grow>
                    <LuisStepper steps={modalSteps} onStepClick={onStepChange} />
                </Stack.Item>
                <Stack.Item grow>{getStepContent()}</Stack.Item>
            </Stack>
        </BasicModal>
    );
});

const BenefitsScreen = () => {
    const { localize } = useLocalization();

    return (
        <Stack styles={{ root: { minHeight: '380px' } }} tokens={{ childrenGap: 12 }}>
            <LuisText variant="large" fontWeight={600}>
                {localize(messages.benefitsTitle)}
            </LuisText>
            <ShiftedUList>
                <ListItem>
                    <LuisText fontWeight="bold">{localize(messages.stateOfArt)}</LuisText>
                    &nbsp;
                    <LuisText>{localize(messages.stateOfArtDescription)}</LuisText>
                </ListItem>
                <ListItem>
                    <LuisText fontWeight="bold">{localize(messages.robust)}</LuisText>
                    &nbsp;
                    <LuisText>{localize(messages.robustCont)}</LuisText>
                </ListItem>
                <ListItem>
                    <LuisText>{localize(messages.simplerAppExperienceIntro)}</LuisText>
                    &nbsp;
                    <LuisText fontWeight="bold">{localize(messages.simplerAppExperience)}</LuisText>
                </ListItem>
                <ListItem>
                    <LuisText>{localize(messages.testMultiLanguageIntro)}</LuisText>
                    &nbsp;
                    <LuisText fontWeight="bold">{localize(messages.testMultiLanguage)}</LuisText>
                </ListItem>
            </ShiftedUList>
        </Stack>
    );
};

const LimitationsScreen = () => {
    const { localize } = useLocalization();

    return (
        <Stack styles={{ root: { minHeight: '380px' } }} tokens={{ childrenGap: 12 }}>
            <LuisText variant="large" fontWeight={600}>
                {localize(messages.limitationsTitle)}
            </LuisText>
            <ShiftedUList>
                <ListItem>
                    <LuisText>{localize(messages.patternsBullet)}</LuisText>
                </ListItem>
                <ListItem>
                    <LuisText>{localize(messages.phraseListsBullet)}</LuisText>
                </ListItem>
                <ListItem>
                    <LuisText>{localize(messages.normalizationBullet)}</LuisText>
                </ListItem>
                <ListItem>
                    <LuisText>{localize(messages.modelFeaturesBullet)}</LuisText>
                </ListItem>
                <ListItem>
                    <LuisText>{localize(messages.rolesBullet)}</LuisText>
                </ListItem>
                <ListItem>
                    <LuisText>{localize(messages.overlappingLabelsBullet)}</LuisText>
                </ListItem>
                <ListItem>
                    <LuisText>{localize(messages.containerizedRuntimeBullet)}</LuisText>
                </ListItem>
            </ShiftedUList>

            <ExternalLink isUnderlined href="https://aka.ms/luis-private-unsupported-features">
                {localize(messages.learnMoreUnsupportedFeatures)}
            </ExternalLink>
        </Stack>
    );
};
